
import { useState } from 'react';

export default function CallToAction() {
  const [formData, setFormData] = useState({
    firmName: '',
    contactName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/public/form/submit/carfuagency-consultation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          firmName: '',
          contactName: '',
          email: '',
          phone: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="cta" className="bg-[#0E1A2B] py-32">
      <div className="max-w-4xl mx-auto px-8">
        <div className="text-center mb-16">
          <h2 className="font-serif text-4xl md:text-5xl text-[#F7F5F2] mb-6">
            Built for firms that operate at a higher standard.
          </h2>
          <p className="text-xl text-[#8A8F98] leading-relaxed">
            Schedule a confidential consultation to explore how intelligent systems can elevate your firm's operations.
          </p>
        </div>
        
        <form id="consultation-form" data-readdy-form onSubmit={handleSubmit} className="bg-[#1C1C1E] p-12 shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="firmName" className="block text-[#F7F5F2] text-sm font-medium mb-2">
                Law Firm Name
              </label>
              <input
                type="text"
                id="firmName"
                name="firmName"
                value={formData.firmName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0E1A2B] text-[#F7F5F2] border border-[#8A8F98]/30 focus:border-[#C8A14A] focus:outline-none transition-colors text-sm"
              />
            </div>
            <div>
              <label htmlFor="contactName" className="block text-[#F7F5F2] text-sm font-medium mb-2">
                Contact Name
              </label>
              <input
                type="text"
                id="contactName"
                name="contactName"
                value={formData.contactName}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0E1A2B] text-[#F7F5F2] border border-[#8A8F98]/30 focus:border-[#C8A14A] focus:outline-none transition-colors text-sm"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="email" className="block text-[#F7F5F2] text-sm font-medium mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0E1A2B] text-[#F7F5F2] border border-[#8A8F98]/30 focus:border-[#C8A14A] focus:outline-none transition-colors text-sm"
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-[#F7F5F2] text-sm font-medium mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-[#0E1A2B] text-[#F7F5F2] border border-[#8A8F98]/30 focus:border-[#C8A14A] focus:outline-none transition-colors text-sm"
              />
            </div>
          </div>
          
          <div className="mb-8">
            <label htmlFor="message" className="block text-[#F7F5F2] text-sm font-medium mb-2">
              How can we assist your firm?
            </label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={5}
              maxLength={500}
              className="w-full px-4 py-3 bg-[#0E1A2B] text-[#F7F5F2] border border-[#8A8F98]/30 focus:border-[#C8A14A] focus:outline-none transition-colors resize-none text-sm"
            ></textarea>
            <p className="text-[#8A8F98] text-xs mt-2">{formData.message.length}/500 characters</p>
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full px-10 py-4 bg-[#C8A14A] text-[#0E1A2B] text-base font-semibold tracking-wide hover:bg-[#d4af5e] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? 'Submitting...' : 'Request Consultation'}
          </button>
          
          {submitStatus === 'success' && (
            <div className="mt-6 p-4 bg-[#C8A14A]/20 border border-[#C8A14A] text-[#F7F5F2] text-center text-sm">
              Thank you for your inquiry. We will contact you shortly.
            </div>
          )}
          
          {submitStatus === 'error' && (
            <div className="mt-6 p-4 bg-red-900/20 border border-red-500 text-[#F7F5F2] text-center text-sm">
              An error occurred. Please try again or contact us directly.
            </div>
          )}
        </form>
      </div>
    </section>
  );
}
